import React from 'react';
import { useTranslation } from 'react-i18next';
import { JOB_STATUS, JOB_STATUS_ORDER, JOB_STATUS_META, PHASE_LABEL, getStatusIndex, normalizeStatus } from '../constants/jobStatus';

/**
 * StatusSelector — Soroby "Lifecycle Spine" job status control.
 *
 * A vertical timeline of the 8 lifecycle stages, grouped under their 3 phases
 * (Booking / Active / Done). The CURRENT stage is an emphasized, phase-colored
 * card (Now badge, one-line description, live progress meter, "Next:" hint,
 * and Back/Advance steppers for the common adjacent move). Completed history
 * calms into checked rows; the future dims back. Every row is a native
 * <button>, so any stage can be jumped to forward OR back in one tap (and via
 * Tab+Enter for keyboard users) — stages can be skipped freely per job.
 *
 * Stage labels/phases/colors come from `src/constants/jobStatus.js` (single
 * source of truth) rather than being defined locally.
 *
 * Controlled: ALL display derives from `value`. Unknown/empty -> first stage.
 * When `value` is CANCELLED, the spine freezes read-only at `cancelledFromStatus`
 * (the stage the job was in when it was cancelled) and a Cancelled banner is
 * shown instead of the normal header/footer — CANCELLED is not itself a stage.
 *
 * Optional `onResume(status)` puts a cancelled job back into the workflow. It
 * lives here rather than beside the Cancel button because the Cancel button is
 * in the desktop-only summary pane, whereas this control renders on desktop AND
 * in the mobile wizard's Status step — so this is the one spot that reaches
 * both. It hands back the stage the frozen spine is displaying, so what the
 * user sees marked as reached is exactly where the job resumes.
 * API: const StatusSelector = ({ value, onChange, cancelledFromStatus, onResume }) => { ... }
 *
 * Styles live in soroby-redesign.css (loaded globally), scoped `.sbr .jsel-*`.
 */

// Built per render, not at import: labels must follow the active language.
const getStages = () => JOB_STATUS_ORDER.map((value) => {
    const meta = JOB_STATUS_META[value];
    return { value, label: meta.label, phase: PHASE_LABEL[meta.phase], tone: meta.tone, desc: meta.desc };
});

/* ── inline icons (no libs) ─────────────────────────────────────────── */
const CheckIcon = () => (
    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" aria-hidden="true">
        <path d="M5 12.5l4.2 4.2L19 7" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const ArrowIcon = () => (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" aria-hidden="true">
        <path d="M5 12h13M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const RotateIcon = () => (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" aria-hidden="true">
        <path d="M3 12a9 9 0 1 0 9-9 9.7 9.7 0 0 0-6.7 2.7L3 8" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M3 3v5h5" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const ChevronIcon = ({ dir }) => (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" aria-hidden="true">
        {dir === 'up'
            ? <path d="M6 15l6-6 6 6" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" />
            : <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" />}
    </svg>
);

const StatusSelector = ({ value, onChange, cancelledFromStatus, onResume }) => {
    const { t } = useTranslation();
    const stages = getStages();

    // Cancelled is not a stage in the linear spine — it's an exceptional
    // side-state applied from any stage. Render a frozen, read-only spine
    // at the last real stage plus a Cancelled banner instead of the normal
    // interactive header/footer.
    if (value === JOB_STATUS.CANCELLED) {
        const frozenIndex = Math.max(0, getStatusIndex(cancelledFromStatus));
        const frozenFillPct = stages.length > 1 ? (frozenIndex / (stages.length - 1)) * 100 : 0;

        return (
            <div className="sbr">
                <div className="jsel-wrap jsel-tone-cancelled jsel-cancelled" data-done="false">
                    <div className="jsel-head">
                        <span className="jsel-head-label">{t('jobs.status')}</span>
                        <span className="jsel-head-pill jsel-head-pill-cancelled">
                            <i className="jsel-pill-dot" />
                            {t('customerStages.CANCELLED')}
                        </span>
                    </div>

                    <ol className="jsel-spine jsel-spine-frozen" style={{ '--jsel-fill': `${frozenFillPct}%` }}>
                        <span className="jsel-rail" aria-hidden="true" />
                        <span className="jsel-rail-fill" aria-hidden="true" />

                        {stages.map((stage, i) => {
                            const past = i <= frozenIndex;
                            const phaseStart = i === 0 || stages[i - 1].phase !== stage.phase;

                            return (
                                <React.Fragment key={stage.value}>
                                    {phaseStart && (
                                        <li className="jsel-phase" aria-hidden="true">
                                            <span className="jsel-phase-tick" />
                                            <span className="jsel-phase-name">{stage.phase}</span>
                                        </li>
                                    )}
                                    <li className="jsel-row-wrap">
                                        <span className={`jsel-row jsel-${past ? 'done' : 'future'} jsel-tone-${stage.tone}`}>
                                            <span className="jsel-node" aria-hidden="true">
                                                <span className="jsel-node-core">
                                                    {past ? <CheckIcon /> : <span className="jsel-node-pip" />}
                                                </span>
                                            </span>
                                            <span className="jsel-body">
                                                <span className="jsel-line">
                                                    <span className="jsel-label">{stage.label}</span>
                                                </span>
                                            </span>
                                        </span>
                                    </li>
                                </React.Fragment>
                            );
                        })}
                    </ol>

                    {/* Cancelling used to be a dead end — the spine is read-only
                        here, so without this there is no way back into the
                        workflow. Resuming returns the job to the stage it was
                        cancelled from, which is the one the frozen spine shows
                        as reached. */}
                    {typeof onResume === 'function' && (
                        <div className="jsel-foot">
                            <button
                                type="button"
                                className="jsel-step jsel-resume"
                                title={t('statusSelector.resumeTitle', { stage: stages[frozenIndex].label })}
                                onClick={() => onResume(stages[frozenIndex].value)}
                            >
                                <RotateIcon />
                                <span className="jsel-step-tx">{t('statusSelector.resumeAtStage', { stage: stages[frozenIndex].label })}</span>
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Derive everything from value; accepts either a new canonical status or
    // a legacy pre-redesign one (normalized transparently); unknown/empty -> first stage.
    const knownIndex = stages.findIndex((s) => s.value === normalizeStatus(value));
    const currentIndex = knownIndex === -1 ? 0 : knownIndex;
    const current = stages[currentIndex];
    const tone = current.tone;

    const prev = currentIndex > 0 ? stages[currentIndex - 1] : null;
    const next = currentIndex < stages.length - 1 ? stages[currentIndex + 1] : null;
    const isDone = current.value === JOB_STATUS.COMPLETED;

    // Position within the lifecycle, for the rail fill + header count + meter.
    const fillPct = stages.length > 1 ? (currentIndex / (stages.length - 1)) * 100 : 0;

    const select = (v) => {
        if (v !== current.value && typeof onChange === 'function') onChange(v);
    };

    return (
        <div className="sbr">
            <div className={`jsel-wrap jsel-tone-${tone}`} data-done={isDone ? 'true' : 'false'}>
                {/* header: label + live phase-toned summary pill */}
                <div className="jsel-head">
                    <span className="jsel-head-label">{t('jobs.status')}</span>
                    <span className="jsel-head-pill">
                        <i className="jsel-pill-dot" />
                        {current.label}
                        <span className="jsel-pill-meta">
                            {currentIndex + 1}<span className="jsel-pill-slash">/</span>{stages.length}
                        </span>
                    </span>
                </div>

                {/* the spine */}
                <ol className="jsel-spine" style={{ '--jsel-fill': `${fillPct}%` }}>
                    <span className="jsel-rail" aria-hidden="true" />
                    <span className="jsel-rail-fill" aria-hidden="true" />

                    {stages.map((stage, i) => {
                        const isCurrent = i === currentIndex;
                        const past = i < currentIndex;
                        const future = i > currentIndex;
                        const state = isCurrent ? 'current' : past ? 'done' : 'future';
                        const phaseStart = i === 0 || stages[i - 1].phase !== stage.phase;

                        return (
                            <React.Fragment key={stage.value}>
                                {phaseStart && (
                                    <li className="jsel-phase" aria-hidden="true">
                                        <span className="jsel-phase-tick" />
                                        <span className="jsel-phase-name">{stage.phase}</span>
                                    </li>
                                )}

                                <li className="jsel-row-wrap">
                                    <button
                                        type="button"
                                        className={`jsel-row jsel-${state} jsel-tone-${stage.tone}`}
                                        aria-current={isCurrent ? 'step' : undefined}
                                        aria-pressed={isCurrent}
                                        title={future ? t('statusSelector.moveToStage', { stage: stage.label }) : past ? t('statusSelector.backToStage', { stage: stage.label }) : stage.label}
                                        onClick={() => select(stage.value)}
                                    >
                                        <span className="jsel-node" aria-hidden="true">
                                            <span className="jsel-node-core">
                                                {past ? <CheckIcon /> : <span className="jsel-node-pip" />}
                                            </span>
                                            {isCurrent && <span className="jsel-node-ring" />}
                                        </span>

                                        <span className="jsel-body">
                                            <span className="jsel-line">
                                                <span className="jsel-label">{stage.label}</span>
                                                {isCurrent && <span className="jsel-now">{t('statusSelector.now')}</span>}
                                            </span>

                                            {isCurrent && (
                                                <>
                                                    <span className="jsel-hint">{stage.desc}</span>
                                                    <span className="jsel-meter" aria-hidden="true">
                                                        <span className="jsel-meter-fill" style={{ width: `${fillPct}%` }} />
                                                    </span>
                                                    <span className="jsel-next">
                                                        {isDone
                                                            ? <><span className="jsel-next-check"><CheckIcon /></span>{t('statusSelector.lifecycleComplete')}</>
                                                            : <>{t('statusSelector.next')} <strong>{next.label}</strong></>}
                                                    </span>
                                                </>
                                            )}
                                        </span>

                                        <span className="jsel-action" aria-hidden="true">
                                            {isCurrent ? (
                                                <span className="jsel-action-dot" />
                                            ) : (
                                                <span className="jsel-jump">
                                                    {future ? t('statusSelector.moveHere') : t('statusSelector.backHere')}
                                                    <ArrowIcon />
                                                </span>
                                            )}
                                        </span>
                                    </button>
                                </li>
                            </React.Fragment>
                        );
                    })}
                </ol>

                {/* footer: one-tap Back / Advance for the common adjacent move */}
                <div className="jsel-foot">
                    <button
                        type="button"
                        className="jsel-step"
                        disabled={!prev}
                        title={prev ? t('statusSelector.backToStage', { stage: prev.label }) : t('statusSelector.atFirstStage')}
                        aria-label={prev ? t('statusSelector.backToStage', { stage: prev.label }) : t('statusSelector.atFirstStage')}
                        onClick={() => prev && select(prev.value)}
                    >
                        <ChevronIcon dir="up" />
                        <span className="jsel-step-tx">{t('common.back')}</span>
                    </button>
                    <button
                        type="button"
                        className="jsel-step jsel-step-fwd"
                        disabled={!next}
                        title={next ? t('statusSelector.advanceToStage', { stage: next.label }) : t('statusSelector.atFinalStage')}
                        aria-label={next ? t('statusSelector.advanceToStage', { stage: next.label }) : t('statusSelector.atFinalStage')}
                        onClick={() => next && select(next.value)}
                    >
                        <span className="jsel-step-tx">{t('statusSelector.advance')}</span>
                        <ChevronIcon dir="down" />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default StatusSelector;
